import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { Carousel } from 'react-responsive-carousel';
import { useDispatch } from 'react-redux';
import { closeModal } from 'redux/modal';
import OnboardingStepOne from './OnboardingStepOne';
import OnboardingStepTwo from './OnboardingStepTwo';
import OnboardingStepThree from './OnboardingStepThree';
import Icon from './Icon';
import Button from './Button';
import OnboardingStepFour from './OnboardingStepFour';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

const Onboarding = (props) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const dispatch = useDispatch();

  const videoRef = useRef([]);

  const slides = [
    OnboardingStepOne,
    OnboardingStepTwo,
    OnboardingStepThree,
    OnboardingStepFour,
  ];

  const continueClickHandler = () => {
    const nextSlide = currentSlide + 1

    if (nextSlide < slides.length) {
      setCurrentSlide(nextSlide);
    } else {
      dispatch(closeModal('onboarding'));
      localStorage.setItem('isOnboardingComplete', 1);
    }
  };

  const updateCurrentSlideHandler = useCallback((index) => {
    if (currentSlide !== index) {
      setCurrentSlide(index);
    }

    const video = videoRef.current[index];

    if (video) {
      video.currentTime = 0;
      video.play();
    }
  }, [currentSlide]);

  const getButtonText = () => {
    if (currentSlide === slides.length - 1) {
      return 'Get started';
    }

    return 'Continue';
  }

  const assignVideoRef = useCallback((ref, index) => {
    videoRef.current[index] = ref;
  }, []);

  const isVideoDisplayed = currentSlide !== 0;

  return (
    <Wrapper hasVideo={isVideoDisplayed}>
      <Carousel
        selectedItem={currentSlide}
        showThumbs={false}
        statusFormatter={() => null}
        onChange={updateCurrentSlideHandler}
        renderArrowPrev={(onClickHandler, hasPrev, label) => (
            <Control onClick={onClickHandler} pos='left' disabled={!hasPrev}>
              <Icon source='material' name='chevron_left' />
            </Control>
        )}
        renderArrowNext={(onClickHandler, hasNext, label) => (
          <Control onClick={onClickHandler} pos='right' disabled={!hasNext}>
            <Icon source='material' name='chevron_right' />
          </Control>
        )}
        renderIndicator={(onClickHandler, isSelected, index, label) => {
            return (
              <Indicator
                isSelected={isSelected}
                onClick={onClickHandler}
              />
            )
          }}
        >
          {slides.map((Slide, index) => (
            <Slide videoRef={(ref) => assignVideoRef(ref, index)} key={index} /> ))
          }
      </Carousel>

      <Button
        width='medium'
        onClick={continueClickHandler}
      >
        {getButtonText()}
      </Button>
    </Wrapper>
  )
}

export default Onboarding;

const Wrapper = styled.div`
  position: relative;
  width: 530px;
  padding: 6px 6px 35px;

  &::before {
    position: absolute;
    content: '';
    background: radial-gradient(circle, rgba(179, 111, 217, 0.65) 10%, rgba(0, 0, 0, 0) 60%);
    width: 131%;
    height: 120%;
    top: -65%;
    left: -50%;
    z-index: -1;
  }

  &::after {
    position: absolute;
    content: '';
    background: radial-gradient(circle, rgba(113, 198, 255, 0.7) 10%, rgba(0, 0, 0, 0) 60%);
    width: 150%;
    height: 120%;
    top: -80%;
    right: -40%;
    z-index: -1;
  }

  .carousel.carousel-slider {
    --carousel-height: 380px;
    overflow: unset;
    height: var(--carousel-height);
    padding-bottom: 30px;
  }

  .carousel .control-dots {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  ${Button} {
    font-weight: 600;
    border-radius: 12px;
    font-family: 'Inter';
    margin: 15px auto 0;
  }
`;

const Control = styled.button`
  all: unset;
  position: absolute;
  width: 32px;
  height: 32px;
  border-radius: 32px;
  background-color: var(--lizzy-white);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  filter: drop-shadow(0px -10px 27px rgba(0, 0, 0, 0.09))
    drop-shadow(0px 3.75998px 11.28px rgba(0, 0, 0, 0.0503198))
    drop-shadow(0px 2.01027px 6.0308px rgba(0, 0, 0, 0.0417275))
    drop-shadow(0px 1.12694px 3.38082px rgba(0, 0, 0, 0.035))
    drop-shadow(0px 0.598509px 1.79553px rgba(0, 0, 0, 0.0282725))
    drop-shadow(0px 0.249053px 0.747159px rgba(0, 0, 0, 0.0196802));

  bottom: 0;
  ${p => p.pos === 'right' ? 'right: 25px' : 'left: 25px'};
  z-index: 2;

  &:disabled {
    cursor: default;
    ${Icon} {
      color: var(--lizzy-black-10);
    }
  }
`;

const Indicator = styled.div`
  width: 8px;
  height: 8px;
  background: ${p => p.isSelected ? 'var(--lizzy-blue)' : 'var(--lizzy-black-10)'} ;
  border-radius: 8px;
  cursor: pointer;

  &:not(:last-child) {
    margin-right: 10px;
  }
`;